import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useGetNote, useGetUser, useUpdateNote } from '@/services/noteGoApi/noteGoApiComponents';
import { queryKeyFn } from '@/services/noteGoApi/noteGoApiContext';
import { useAuthStore } from '@/stores/authStore';
import useAlertStore from '@/stores/alertStore';
import useFetchTranscript from '@/hooks/useFetchTranscript';
import useRealTimeProcessingType from '@/hooks/useRealTimeProcessingType';
import useProcessingTitles from '@/hooks/useProcessingTitles';
import { FIREBASE_STORAGE_ERRORS, GET_NOTE_ERRORS, truncateErrorMessage } from '@/helpers/errors';
import { resolveMediaUrl } from '@/utils/firebaseUtils';
import { InitialAssessment, NoteOut, NoteUpdate } from '@/services/noteGoApi/noteGoApiSchemas';
import { getTrialStatus } from '@/helpers/trialStatus';
import { usePlayTracking } from '@/hooks/usePlayTracking';
import { parseTime } from '@/helpers/formatTime';
import { RouteMap } from '@/routes/Routes';
import { SummaryFeedbackValue } from '../components/SummaryFeedback/SummaryFeedback';
import { hasSubscriptionProduct } from '@/utils/subscriptionHelpers';
import { logger } from '@/services/loggerService';
import { errorReporter, isNetworkError } from '@/services/errorReportingService';
import { DEFAULT_TITLE_PLACEHOLDER, DEFAULT_SUMMARY_PLACEHOLDER } from '../constants';
import { DocumentChapter, Transcript } from '@/types/note';
import { useAutoTag } from '@/features/betaSmartTags/hooks/useAutoTag';

const TRIAL_SHARE_ARROW_THRESHOLD = 5;

const getValidatedSummaryFeedback = (value: NoteOut['summary_feedback']): SummaryFeedbackValue =>
  value === 0 || value === 1 ? value : null;

// Shared timer for video loading instrumentation — reset on each note page mount
let videoLoadT0 = 0;
const vt = (label: string, data?: Record<string, unknown>) => {
  if (!videoLoadT0) videoLoadT0 = performance.now();
  const ms = (performance.now() - videoLoadT0).toFixed(0);
  logger.debug('video', `[+${ms}ms] ${label}`, data);
};

export const useNotePageData = (noteId: string | undefined) => {
  const { userToken } = useAuthStore();
  const navigation = useNavigate();
  const queryClient = useQueryClient();
  const showAlert = useAlertStore((state) => state.showAlert);
  const [isOwner, setIsOwner] = useState(false);
  const [signedUrl, setSignedUrl] = useState('');
  const [noteData, setNoteData] = useState<NoteOut | null>(null);
  const [currentTitle, setCurrentTitle] = useState<string>('');
  const [currentFullTranscriptList, setCurrentFullTranscriptList] = useState<Transcript[] | null>(null);
  const [isRefetchingAfterProcessing, setIsRefetchingAfterProcessing] = useState(false);
  const [signedThumbnailUrl, setSignedThumbnailUrl] = useState<string | undefined>();
  const [completedProcessingNoteId, setCompletedProcessingNoteId] = useState<string | null>(null);

  // Track whether the note was genuinely in a processing state (processing_type === null
  // in the API response). Prevents a false refetch on initial mount where rawIsProcessing
  // briefly appears true because noteData hasn't loaded yet (processing_type is undefined).
  const wasGenuinelyProcessing = useRef(false);

  // Reset timer, processing-completion flag, and genuine-processing ref on each note change
  useEffect(() => {
    videoLoadT0 = performance.now();
    setCompletedProcessingNoteId(null);
    wasGenuinelyProcessing.current = false;
    // Clear the previous note's data so navigating A -> B never renders A's
    // content/media while B loads (or forever, if B's fetch fails).
    // wasGenuinelyProcessing is reset above, so the resulting transient
    // rawIsProcessing=true (processing_type undefined) cannot trigger the
    // processing-completed refetch/auto-tag paths.
    setNoteData(null);
    setSignedUrl('');
    setSignedThumbnailUrl(undefined);
    setCurrentTitle('');
    vt('useNotePageData MOUNT', { noteId });
    return () => vt('useNotePageData UNMOUNT');
  }, [noteId]);

  const { mutateAsync: updateNote } = useUpdateNote();

  const { data: userData, isLoading: isLoadingUser } = useGetUser({
    headers: { authorization: `Bearer ${userToken}` },
  });

  const {
    data,
    error,
    isLoading: isLoadingNote,
    refetch: refetchNote,
  } = useGetNote(
    {
      headers: { authorization: `Bearer ${userToken}` },
      pathParams: { noteId: noteId || '' },
    },
    { retry: false },
  );

  // Determine if user is on free plan (no AI processing happens for free users)
  // Only set isFreePlan when userData has loaded to avoid race condition
  const isFreePlan = !isLoadingUser && hasSubscriptionProduct(userData, 'FREE');

  // Get raw processing state from Firebase
  const rawIsProcessing = useRealTimeProcessingType(noteId, noteData?.processing_type);

  // For free plan users, there's no AI processing - skip AI processing indicators
  const isProcessing = isFreePlan ? false : rawIsProcessing;
  // Note-level processing state (used for title/summary/edit gating regardless of plan)
  const isNoteProcessing = rawIsProcessing;

  // Log processing state transitions
  const prevRawProcessing = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (prevRawProcessing.current !== rawIsProcessing) {
      vt('processing state', {
        rawIsProcessing,
        isProcessing,
        isNoteProcessing,
        noteDataLoaded: Boolean(noteData),
        processingType: noteData?.processing_type ?? '(null)',
        wasGenuinelyProcessing: wasGenuinelyProcessing.current,
      });
      prevRawProcessing.current = rawIsProcessing;
    }
  });
  const { currentMessage: processingMessage, isVisible: processingTitleVisible } = useProcessingTitles(isProcessing);

  // Auto-tag note when processing completes (ESSENTIALS+ plan, Smart Tags enabled)
  const handleAutoTagRefetch = useCallback(() => {
    refetchNote().catch((error) => {
      errorReporter.expected('Failed to refetch note after auto-tag', error, {
        feature: 'smartTags',
        operation: 'autoTagRefetch',
        noteId,
      });
    });
  }, [refetchNote, noteId]);

  useAutoTag({
    noteId,
    processingJustCompleted: completedProcessingNoteId === noteId,
    noteHasTags: (noteData?.tags?.length ?? 0) > 0,
    onTagsApplied: handleAutoTagRefetch,
  });

  const {
    transcriptList,
    error: transcriptError,
    isLoading: isLoadingTranscript,
  } = useFetchTranscript(noteData?.transcript_url || null);

  const {
    transcriptList: fetchedFullTranscriptList,
    error: fullTranscriptError,
    isLoading: isLoadingFullTranscript,
  } = useFetchTranscript(noteData?.full_transcript_url || null);

  useEffect(() => {
    if (!noteId) {
      navigation(RouteMap.HOME);
    }
  }, [noteId, navigation]);

  // Mark the note as genuinely processing when API confirms processing_type is null
  useEffect(() => {
    if (noteData && noteData.processing_type === null) {
      wasGenuinelyProcessing.current = true;
    }
  }, [noteData]);

  useEffect(() => {
    const getNewNoteData = async () => {
      setIsRefetchingAfterProcessing(true);
      try {
        const result = await refetchNote();
        // React Query v5 `refetch()` RESOLVES with `{ data, error }` instead of
        // rejecting, so the catch below never sees a failed refetch. Blindly doing
        // `setNoteData(result?.data || null)` wiped the note on a transient 5xx:
        // summary/chapters/tags vanished (and the `[data]` effect never re-fires to
        // restore them) while the video kept playing under an empty page.
        // Keep the last-known-good note and tell the user instead.
        if (result?.data) {
          setNoteData(result.data);
        } else {
          errorReporter.expected('Refetch after processing returned no data', result?.error, {
            feature: 'notes',
            operation: 'refetchAfterProcessing',
            noteId,
          });
          showAlert('Could not refresh the note. Reload the page to see the latest version.', 'error');
        }
      } catch (err) {
        errorReporter.expected('Failed to refetch note after processing completed', err, {
          feature: 'notes',
          operation: 'refetchAfterProcessing',
          noteId,
        });
        showAlert('Error fetching the note. Try again later.', 'error');
      } finally {
        setIsRefetchingAfterProcessing(false);
      }
    };

    // Only refetch when processing genuinely transitions from active to done.
    // rawIsProcessing starts true on mount (noteData is null → processing_type is undefined),
    // then flips to false when data arrives with a set processing_type. Without the
    // wasGenuinelyProcessing guard, this false transition triggers an unnecessary refetch
    // on every page load for already-completed notes.
    if (!rawIsProcessing && wasGenuinelyProcessing.current) {
      wasGenuinelyProcessing.current = false;
      setCompletedProcessingNoteId(noteId ?? null);
      getNewNoteData();
    }
  }, [rawIsProcessing, refetchNote, showAlert]);

  useEffect(() => {
    if (data) {
      vt('React Query data arrived', {
        processingType: data.processing_type ?? null,
        hasVideoUrl: Boolean(data.video_url),
        hasThumbnailUrl: Boolean(data.thumbnail_url),
      });
      setNoteData(data);
      setCurrentTitle(data.title || '');
    }
  }, [data]);

  useEffect(() => {
    setCurrentFullTranscriptList(fetchedFullTranscriptList);
  }, [fetchedFullTranscriptList]);

  useEffect(() => {
    if (!noteData) return;

    logger.debug('notes', 'Note payload loaded for Note page', {
      noteId: noteData.id,
      processingType: noteData.processing_type ?? null,
      documentChapterCount: noteData.document_chapter?.length ?? 0,
      keyMomentsCount: noteData.key_moments?.length ?? 0,
      hasTranscriptUrl: Boolean(noteData.transcript_url),
      hasFullTranscriptUrl: Boolean(noteData.full_transcript_url),
      tagsCount: noteData.tags?.length ?? 0,
    });
  }, [noteData]);

  useEffect(() => {
    let isActive = true;

    const fetchSecureUrl = async (filePath: string) => {
      vt('fetchSecureUrl START', { filePath });
      try {
        const downloadURL = await resolveMediaUrl(filePath);
        if (!downloadURL) {
          vt('fetchSecureUrl DONE (empty)', { isActive });
          return;
        }
        vt('fetchSecureUrl DONE', { isActive, urlLength: downloadURL.length });
        if (isActive) setSignedUrl(downloadURL);
      } catch (error: unknown) {
        if (!isActive) return;
        const firebaseError = error as { code?: string };
        const errorCode = firebaseError?.code || '';

        if (
          errorCode === FIREBASE_STORAGE_ERRORS.UNAUTHORIZED ||
          errorCode === FIREBASE_STORAGE_ERRORS.UNAUTHENTICATED
        ) {
          showAlert(
            'You do not have permission to view this content. Please make sure you are logged in with the correct account that has access to this content.',
            'error',
          );
        } else if (errorCode === FIREBASE_STORAGE_ERRORS.OBJECT_NOT_FOUND) {
          showAlert('The media file could not be found. It may have been deleted or moved.', 'error');
        } else {
          showAlert('Error getting the note content.', 'error');
        }
      }
    };

    if (noteData) {
      const filePath = noteData.video_url || noteData.audio_url || '';
      vt('signedUrl effect fired', { filePath: filePath || '(none)', hasThumbnail: Boolean(noteData.thumbnail_url) });
      if (filePath) {
        fetchSecureUrl(filePath);
      } else {
        setSignedUrl('');
      }

      // Fetch thumbnail URL independently - silent failure (progressive enhancement)
      if (noteData.thumbnail_url) {
        vt('thumbnail fetch START', { path: noteData.thumbnail_url });
        resolveMediaUrl(noteData.thumbnail_url)
          .then((url) => {
            if (!url) return;
            vt('thumbnail fetch DONE', { isActive, urlLength: url.length });
            if (isActive) setSignedThumbnailUrl(url);
          })
          .catch(() => {
            // Silently ignore - older notes may not have thumbnails
          });
      } else {
        setSignedThumbnailUrl(undefined);
      }
    }

    return () => {
      isActive = false;
    };
  }, [noteData, showAlert]);

  useEffect(() => {
    if (error) {
      const apiError = error as { detail?: string };
      const errorMessage = typeof apiError?.detail === 'string' ? apiError.detail : undefined;

      // Only redirect on confirmed access-denied errors — other `detail` strings
      // (transient 5xx, rate limits, etc.) should not send users away from the page
      const isAccessDenied =
        errorMessage === GET_NOTE_ERRORS.NOTE_DOES_NOT_BELONG_TO_USER ||
        errorMessage === GET_NOTE_ERRORS.NOTE_NOT_FOUND ||
        errorMessage === GET_NOTE_ERRORS.INVITATION_EXPIRED;

      if (isAccessDenied) {
        navigation(RouteMap.HOME);
        showAlert(truncateErrorMessage(errorMessage), 'error', null);
      } else {
        showAlert(
          truncateErrorMessage(errorMessage) || 'Something went wrong while fetching the note. Try again later.',
          'error',
        );
      }
    }

    if (transcriptError || fullTranscriptError) {
      logger.warn('notes', 'Transcript fetch error on Note page', {
        noteId: noteData?.id ?? noteId ?? null,
        transcriptError,
        fullTranscriptError,
      });
      showAlert(truncateErrorMessage(transcriptError || fullTranscriptError), 'error');
    }
  }, [error, fullTranscriptError, navigation, noteData?.id, noteId, showAlert, transcriptError]);

  useEffect(() => {
    if (!userData || !noteData) return;
    setIsOwner(userData.id === noteData.creator_user_id);
  }, [userData, noteData]);

  const handleTitleUpdate = (newTitle: string) => {
    setCurrentTitle(newTitle);
    if (noteData) {
      setNoteData({ ...noteData, title: newTitle });
    }
    refetchNote().catch((error) => {
      errorReporter.expected('Failed to refetch note after title update', error, { feature: 'notes' });
      showAlert('Title saved, but failed to refresh note data. Try refreshing the page.', 'warning');
    });
  };

  const handleFeedbackChange = (value: SummaryFeedbackValue) => {
    setNoteData((prev) => (prev ? { ...prev, summary_feedback: value ?? null } : prev));
  };

  // Write a saved edit into the getNote cache. Any cache change (e.g. a new thumbnail)
  // re-runs the `[data]` effect, which copies the cache over page state — a stale
  // cache would roll the saved edit back on screen and in the next save.
  const patchNoteCache = (fields: Partial<NoteOut>) => {
    if (!noteId) return;
    const queryKey = queryKeyFn({
      path: '/notes/{noteId}',
      operationId: 'getNote',
      variables: { pathParams: { noteId }, headers: { authorization: `Bearer ${userToken}` } },
    });
    queryClient.setQueryData(queryKey, (oldData: NoteOut | undefined) =>
      oldData ? { ...oldData, ...fields } : oldData,
    );
  };

  const handleDocumentChaptersUpdate = async (chapters: DocumentChapter[]) => {
    setNoteData((prev) => (prev ? { ...prev, document_chapter: chapters as NoteOut['document_chapter'] } : prev));

    if (!noteId || !userToken) return;

    const apiChapters = chapters.map((c) => ({
      timecode: parseTime(c.timecode),
      text: c.text,
      summary: c.summary,
    }));

    try {
      await updateNote({
        headers: { authorization: `Bearer ${userToken}` },
        pathParams: { noteId },
        body: { document_chapter: apiChapters },
      });
      patchNoteCache({ document_chapter: chapters as NoteOut['document_chapter'] });
      showAlert('Chapters updated successfully.', 'success');
    } catch (error) {
      if (isNetworkError(error)) {
        errorReporter.expected('Failed to save chapters (network)', error, {
          feature: 'notes',
          operation: 'saveChapters',
          noteId,
        });
      } else {
        errorReporter.critical('Failed to save chapters', error, {
          feature: 'notes',
          operation: 'saveChapters',
          noteId,
        });
      }
      showAlert('Something went wrong while updating chapters. Try again later.', 'error');
      throw error;
    }
  };

  const handleKeyPointsUpdate = (keyPoints: Transcript[]) => {
    setCurrentFullTranscriptList(keyPoints);
  };

  const handleContentAnalysisUpdate = async (assessment: InitialAssessment) => {
    if (!noteId || !userToken) {
      showAlert('Unable to save. Please refresh the page and try again.', 'error');
      throw new Error('Missing noteId or userToken');
    }

    const nextContentAnalysis = { initial_assessment: assessment };
    setNoteData((prev) => (prev ? { ...prev, content_analysis: nextContentAnalysis } : prev));

    try {
      await updateNote({
        headers: { authorization: `Bearer ${userToken}` },
        pathParams: { noteId },
        // content_analysis isn't in the generated NoteUpdate schema yet, but the
        // backend persists it on the note document.
        body: { content_analysis: nextContentAnalysis } as unknown as NoteUpdate,
      });
      patchNoteCache({ content_analysis: nextContentAnalysis });
      showAlert('Content analysis updated successfully.', 'success');
    } catch (error) {
      if (isNetworkError(error)) {
        errorReporter.expected('Failed to save content analysis (network)', error, {
          feature: 'notes',
          operation: 'saveContentAnalysis',
          noteId,
        });
      } else {
        errorReporter.critical('Failed to save content analysis', error, {
          feature: 'notes',
          operation: 'saveContentAnalysis',
          noteId,
        });
      }
      showAlert('Something went wrong while updating the content analysis. Try again later.', 'error');
      throw error;
    }
  };

  const isAudioOnly = noteData?.processing_type === 'audio';

  // Title logic: show rotating message for paid processing; static message for free processing
  // When refetching after processing completes, keep showing a loading state to avoid
  // briefly flashing the placeholder before the AI-generated title arrives
  const noteTitle = isProcessing
    ? processingMessage
    : isNoteProcessing
      ? 'The video is processing.'
      : isRefetchingAfterProcessing && !currentTitle
        ? 'Loading title...'
        : currentTitle || DEFAULT_TITLE_PLACEHOLDER;

  // Summary logic: show processing message while the note is processing
  const noteSummary = isNoteProcessing
    ? 'Processing.'
    : isRefetchingAfterProcessing && !noteData?.summary
      ? 'Loading summary...'
      : noteData?.summary || DEFAULT_SUMMARY_PLACEHOLDER;

  const shouldShowShareArrow = useMemo(() => {
    if (!isOwner || !userData) {
      return false;
    }

    // Always show the arrow during processing to guide the user while AI generates content
    // Use isNoteProcessing (raw state) so it works for all users regardless of plan
    if (isNoteProcessing) {
      return true;
    }

    // After processing, only show for trial users within video count threshold
    const trialStatus = getTrialStatus(userData);
    if (!trialStatus.isOnTrial) {
      return false;
    }

    const subscriptionVideoCount = userData.subscription?.current_period_video_count;
    const videoCount = typeof subscriptionVideoCount === 'number' ? subscriptionVideoCount : 0;

    return videoCount <= TRIAL_SHARE_ARROW_THRESHOLD;
  }, [isOwner, userData, isNoteProcessing]);

  const validatedSummaryFeedback = useMemo(
    () => getValidatedSummaryFeedback(noteData?.summary_feedback ?? null),
    [noteData?.summary_feedback],
  );

  useEffect(() => {
    logger.debug('notes', 'Note page rendering state', {
      noteId: noteData?.id ?? noteId ?? null,
      isFreePlan,
      rawIsProcessing,
      isProcessing,
      isNoteProcessing,
      transcriptCount: transcriptList?.length ?? 0,
      fullTranscriptCount: currentFullTranscriptList?.length ?? 0,
      documentChapterCount: noteData?.document_chapter?.length ?? 0,
      keyMomentsCount: noteData?.key_moments?.length ?? 0,
    });
  }, [
    currentFullTranscriptList?.length,
    isFreePlan,
    isNoteProcessing,
    isProcessing,
    noteData?.document_chapter?.length,
    noteData?.id,
    noteData?.key_moments?.length,
    noteId,
    rawIsProcessing,
    transcriptList?.length,
  ]);

  const { trackPlay } = usePlayTracking({
    note: noteData,
    isShared: false,
  });

  return {
    noteData,
    signedUrl,
    signedThumbnailUrl,
    isOwner,
    userData,
    isProcessing,
    isNoteProcessing,
    processingTitleVisible,
    isLoadingNote,
    isLoadingTranscript,
    isLoadingFullTranscript,
    transcriptList,
    fullTranscriptList: currentFullTranscriptList,
    isAudioOnly,
    noteTitle,
    noteSummary,
    shouldShowShareArrow,
    validatedSummaryFeedback,
    refetchNote,
    handleTitleUpdate,
    handleDocumentChaptersUpdate,
    handleKeyPointsUpdate,
    handleContentAnalysisUpdate,
    handleFeedbackChange,
    trackPlay,
  };
};
